import * as React from "react";
import { EyeIcon, PencilIcon, ExternalLinkIcon, Trash2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

/*
 * Canonical wrapper around shadcn `<Sheet>` for entity detail+edit views
 * (Resources, Items, Flows, Users, Inspections). Rolls together:
 *
 *  - read ↔ edit toggle in the header (icon + aria-label),
 *  - optional "expand to full view" link (only Inspections today),
 *  - sized content area with an optional sticky footer (Save/Cancel),
 *  - sensible mobile defaults: full-width below `sm`, capped above.
 *
 * Width comes from a `size` prop mapped to Tailwind `max-w-*` classes —
 * shadcn's primitive defaults to `sm:max-w-sm` (≈380px) which is too
 * narrow for forms with chips/dynamic-fields, so we default to `lg`.
 *
 * The sheet renders nothing while `open=false`; the underlying Radix
 * primitive handles mount/unmount + portal + focus trap.
 */

export type EntitySheetSize = "sm" | "md" | "lg" | "xl";

interface EntitySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** When true, header shows a Pencil icon and emits onToggleEdit(false). */
  editing?: boolean;
  onToggleEdit?: (next: boolean) => void;
  /** When provided, shows an ExternalLink icon in the header (Inspections). */
  onExpand?: () => void;
  /** Hides the read/edit toggle entirely — e.g. user has no edit permission. */
  canEdit?: boolean;
  /** When provided, shows a destructive Trash icon in the header. The
   *  caller is responsible for wiring it to a ConfirmDialog; this prop
   *  is just the trigger. */
  onDelete?: () => void;
  footer?: React.ReactNode;
  size?: EntitySheetSize;
  children: React.ReactNode;
  /** Forwarded to SheetContent for callers that need extra Tailwind. */
  className?: string;
}

const SIZE_CLASSES: Record<EntitySheetSize, string> = {
  // Mobile (<sm): full-width via the `w-full` baseline. Tablet+ caps via
  // `sm:max-w-*`. `xl` is reserved for media-heavy sheets (e.g. inspections
  // with the resource image).
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
};

export function EntitySheet({
  open,
  onOpenChange,
  title,
  description,
  editing = false,
  onToggleEdit,
  onExpand,
  canEdit = true,
  onDelete,
  footer,
  size = "lg",
  className,
  children,
}: EntitySheetProps) {
  const { t } = useTranslation();
  const showEditToggle = canEdit && typeof onToggleEdit === "function";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // `w-full` ensures full-width mobile; size caps from `sm:` upwards.
        // We disable the primitive's built-in close button — our header
        // already renders one (Radix handles the actual close on outside
        // click / Escape).
        showCloseButton={false}
        className={cn("w-full p-0", SIZE_CLASSES[size], className)}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-border p-4">
            <div className="min-w-0 flex-1">
              <SheetTitle className="truncate text-base">
                {title}
              </SheetTitle>
              {description ? (
                <SheetDescription className="mt-1 truncate">
                  {description}
                </SheetDescription>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center gap-1">
              {showEditToggle ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onToggleEdit?.(!editing)}
                  aria-label={
                    editing
                      ? t("common.view", "View")
                      : t("common.edit", "Edit")
                  }
                >
                  {editing ? (
                    <EyeIcon className="size-4" />
                  ) : (
                    <PencilIcon className="size-4" />
                  )}
                </Button>
              ) : null}

              {onExpand ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onExpand}
                  aria-label={t("common.open-full-view", "Open full view")}
                >
                  <ExternalLinkIcon className="size-4" />
                </Button>
              ) : null}

              {onDelete ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onDelete}
                  aria-label={t("common.delete", "Delete")}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2Icon className="size-4" />
                </Button>
              ) : null}

              {/* Close — bound to the Sheet's open state via onOpenChange. */}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                aria-label={t("common.close", "Close")}
              >
                <span aria-hidden className="text-base leading-none">
                  ×
                </span>
              </Button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4">{children}</div>

          {/* Footer (sticky bottom) */}
          {footer ? (
            <div className="border-t border-border bg-background p-4">
              {footer}
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
